import {
  exportTableHeader,
  isValidSurplusTrimDisplay,
} from '@/constants/surplusTemplate'
import { getLangeKey } from '@/data/i18n/translateLang'
import store from '@/store'
import { matchHighlightBoardName, translate } from '@/util/commonFun'
import { toDecimal } from '@/util/commonFuncs'

import { PartType, PlankType } from '../types/part'

// 样例表头
const sampleHead = ['项目地址', '客户资料', '工厂单号', '房间名称', '订单备注']

// 导入数据必须存在的列
export const indispensableHeaderDataList = [
  translate(getLangeKey('材质')),
  translate(getLangeKey('颜色')),
  translate(getLangeKey('厚度')),
  translate(getLangeKey('成品长度')),
  translate(getLangeKey('成品宽度')),
  translate(getLangeKey('数量')),
  translate(getLangeKey('纹理')),
] as const

export const indispensableHeaderPropList = [
  'matCode',
  'texture',
  'thick',
  'specHeight',
  'specWidth',
  'amount',
  'texDir',
] as const

export type NecessaryTableHeaderType = string

// 根据字段 prop 与当前偏好设置，获取导入表格中对应单元格的值
const getPlankCellValue = (
  plank: Record<string, any>,
  prop: string,
  fallbackHeaders: string[] = []
) => {
  const cfg = store.state.preferencesSetting?.setting?.needSaveData?.find(
    (it) => it.prop === prop
  )
  const keys: string[] = []

  if (cfg?.customLabel) {
    keys.push(cfg.customLabel)
  }
  if (typeof cfg?.label === 'string') {
    keys.push(translate(cfg.label as any))
  }

  keys.push(...fallbackHeaders)

  for (const key of keys) {
    if (!key) continue
    const val = plank[key]
    if (val !== undefined) return val
  }

  return undefined
}

// 根据 prop 与偏好设置，返回 plank 中匹配到的列名（用于需按列名取值的场景，如封边多列）
const getPlankCellKey = (
  plank: Record<string, any>,
  prop: string,
  fallbackHeaders: string[] = []
): string | undefined => {
  const cfg = store.state.preferencesSetting?.setting?.needSaveData?.find(
    (it) => it.prop === prop
  )
  const keys: string[] = []
  if (cfg?.customLabel) keys.push(cfg.customLabel)
  if (typeof cfg?.label === 'string') keys.push(translate(cfg.label as any))
  keys.push(...fallbackHeaders)
  for (const key of keys) {
    if (!key) continue
    if (plank[key] !== undefined) return key
  }
  return undefined
}

// 处理excel表格数据
export const dealExcelData = (data: Record<string, any>[]) => {
  const plankArr: Partial<PlankType>[] = []
  for (let i = 0; i < data.length; ++i) {
    const plank = data[i]
    // 封边列：顺序为 左、后/下、右、前/上；支持自定义表头（列名可能不含“封边”）
    const edgeKeyLeft = getPlankCellKey(plank, 'leftEdge', [
      translate(getLangeKey('左封边')),
    ])
    const edgeKeyBack = getPlankCellKey(plank, 'backEdge', [
      translate(getLangeKey('后封边')),
      translate(getLangeKey('下封边')),
    ])
    const edgeKeyRight = getPlankCellKey(plank, 'rightEdge', [
      translate(getLangeKey('右封边')),
    ])
    const edgeKeyFront = getPlankCellKey(plank, 'frontEdge', [
      translate(getLangeKey('前封边')),
      translate(getLangeKey('上封边')),
    ])
    const edgeKeys = [edgeKeyLeft, edgeKeyBack, edgeKeyRight, edgeKeyFront]
    const texDirCell = getPlankCellValue(plank, 'texDir', [
      translate(getLangeKey('纹理')),
    ])
    const newPlank: Partial<PartType> = {}
    const material = getPlankCellValue(plank, 'matCode', [
      translate(getLangeKey('材质')),
      translate(getLangeKey('材料')),
    ]) as string | undefined
    const thickMatch = material ? material.match(/^\d+(\.\d+)?/) : null
    const thick = thickMatch ? thickMatch[0] : undefined

    newPlank.thick = thick ? Number(thick) : 0
    newPlank.matCode = material ? material.replace(thick ?? '', '') : ''
    let edgeArr: string[] = []
    if (plank[translate(getLangeKey('封边信息'))]) {
      if (plank[translate(getLangeKey('封边信息'))].includes(':')) {
        edgeArr = plank[translate(getLangeKey('封边信息'))]
          .split(':')[1]
          .split(/前|后|左|右|上|下/)
          .filter((v: any) => v && v !== ' ')
      } else if (plank[translate(getLangeKey('封边信息'))].includes(': ')) {
        edgeArr = plank[translate(getLangeKey('封边信息'))]
          .split(': ')[1]
          .split(/前|后|左|右|上|下/)
          .filter((v: any) => v && v !== ' ')
      }
    } else if (edgeKeys.some(Boolean)) {
      edgeArr = edgeKeys.map((key) => (key ? plank[key] : undefined))
    }
    if (edgeArr.length < 4) {
      const n = 4 - edgeArr.length
      edgeArr.push(...new Array(n).fill('0'))
    }
    edgeArr = edgeArr.map((item: any) => {
      let newItem = item?.trim()
      if (!newItem) newItem = '0'
      return newItem
    })
    newPlank.edgeInfo =
      edgeArr.length > 0
        ? `←${edgeArr[0]}↓${edgeArr[1]}→${edgeArr[2]}↑${edgeArr[3]}`
        : ''
    // 支持自定义表头：优先使用 needSaveData 中对应 prop 的 customLabel / label，再回退到默认翻译或旧表头名
    newPlank.plankID = getPlankCellValue(plank, 'plankID', [
      translate(getLangeKey('板号')),
    ])
    newPlank.partName = getPlankCellValue(plank, 'partName', [
      translate(getLangeKey('板件名称')),
      '工件名称',
      translate(getLangeKey('名称')),
    ])
    newPlank.loc = getPlankCellValue(plank, 'loc', [
      translate(getLangeKey('柜体')),
    ])
    newPlank.customer_name = getPlankCellValue(plank, 'customer_name', [
      translate(getLangeKey('客户')),
      'customer',
    ])
    newPlank.frontEdge = getPlankCellValue(plank, 'frontEdge', [
      translate(getLangeKey('前封边')),
    ])?.trim()
    newPlank.backEdge = getPlankCellValue(plank, 'backEdge', [
      translate(getLangeKey('后封边')),
    ])?.trim()
    newPlank.leftEdge = getPlankCellValue(plank, 'leftEdge', [
      translate(getLangeKey('左封边')),
    ])?.trim()
    newPlank.rightEdge = getPlankCellValue(plank, 'rightEdge', [
      translate(getLangeKey('右封边')),
    ])?.trim()
    newPlank.plank_remarks = getPlankCellValue(plank, 'plank_remarks', [
      translate(getLangeKey('板件备注')),
    ])
    // 处理导入excel表格的数据（全部通过 getPlankCellValue 支持自定义表头）
    newPlank.remark = getPlankCellValue(plank, 'remark', ['房间备注']) as any
    newPlank.order_remark = getPlankCellValue(plank, 'order_remark', [
      '订单备注',
    ]) as any
    newPlank.roomName = getPlankCellValue(plank, 'roomName', [
      translate(getLangeKey('房间')),
      'orderRoomName',
    ]) as any
    newPlank.remarks = getPlankCellValue(plank, 'remarks', [
      translate(getLangeKey('备注信息')),
      '备注信息',
      'remarks',
    ]) as any
    for (let remarkIndex = 1; remarkIndex <= 16; remarkIndex++) {
      const remarkProp = `remark${remarkIndex}`
      const remarkLangeKey = `备注信息${remarkIndex}` as Parameters<
        typeof getLangeKey
      >[0]
      ;(newPlank as Record<string, unknown>)[remarkProp] = getPlankCellValue(
        plank,
        remarkProp,
        [translate(getLangeKey(remarkLangeKey)), `备注信息${remarkIndex}`]
      )
    }
    newPlank.orderNo = getPlankCellValue(plank, 'orderNo', [
      translate(getLangeKey('订单号')),
      'orderNo',
    ]) as any
    newPlank.plankNum = getPlankCellValue(plank, 'plankNum', [
      translate(getLangeKey('板件条码')),
    ]) as any
    const doorCell = getPlankCellValue(plank, 'type', [
      translate(getLangeKey('门板')),
    ]) as string | undefined
    newPlank.type =
      doorCell === translate(getLangeKey('非门板')) || !doorCell
        ? 'Plank'
        : 'SingleDoor'
    newPlank.address = getPlankCellValue(plank, 'address', [
      translate(getLangeKey('项目地址')),
      'orderAddress',
    ]) as any
    newPlank.specialShape = getPlankCellValue(plank, 'specialShape', [
      translate(getLangeKey('异形')),
    ]) as any
    // 长宽取值支持自定义表头；横纹时导入文件中“长度”列实际为宽、“宽度”列实际为长，需对调
    const heightVal = getPlankCellValue(plank, 'specHeight', [
      translate(getLangeKey('成品长度')),
      translate(getLangeKey('长度')),
      '长',
    ]) as any
    const widthVal = getPlankCellValue(plank, 'specWidth', [
      translate(getLangeKey('成品宽度')),
      translate(getLangeKey('宽度')),
      '宽',
    ]) as any
    if (texDirCell === translate(getLangeKey('横纹'))) {
      newPlank.specHeight = widthVal
      newPlank.specWidth = heightVal
    } else {
      newPlank.specHeight = heightVal
      newPlank.specWidth = widthVal
    }
    newPlank.thick =
      Number(
        getPlankCellValue(plank, 'thick', [
          translate(getLangeKey('厚度')),
          '厚',
          '开料厚',
        ]) as any
      ) || (thick ? Number(thick) : 0)
    newPlank.oRect = {
      x: 0,
      y: 0,
      width: newPlank.specWidth ? Number(newPlank.specWidth) : 0,
      height: newPlank.specHeight ? Number(newPlank.specHeight) : 0,
    }
    newPlank.fullSize = {}
    newPlank.rect = {
      x: 0,
      y: 0,
      width: toDecimal(
        Number(newPlank.oRect.width) - Number(edgeArr[0]) - Number(edgeArr[2]),
        2
      ),
      height: toDecimal(
        Number(newPlank.oRect.height) - Number(edgeArr[1]) - Number(edgeArr[3]),
        2
      ),
    }
    newPlank.realRect = JSON.parse(JSON.stringify(newPlank.rect))
    newPlank.amount = getPlankCellValue(plank, 'amount', [
      translate(getLangeKey('数量')),
    ]) as any
    newPlank.openDir = getPlankCellValue(plank, 'openDir', [
      translate(getLangeKey('开门方向')),
    ]) as any

    const highGlossCell = getPlankCellValue(plank, 'is_high_gloss_plank', [
      translate(getLangeKey('高光板')),
    ]) as string | undefined
    if (highGlossCell === undefined) {
      // 由于高光别名的问题，这里判断是否是高光还需要根据材质来判断 感觉很容易出错啊，但是好像只能这样
      const arrs = newPlank.matCode?.split('_') || []
      let highGlossName = '高光_'
      if (arrs.length > 1) {
        highGlossName = matchHighlightBoardName(arrs[1])
      }
      // 就根据材质来做初始化
      newPlank.is_high_gloss_plank =
        newPlank.matCode?.includes('高光_') ||
        newPlank.matCode?.includes(highGlossName)
      if (newPlank.is_high_gloss_plank) {
        arrs.shift()
        arrs.unshift('高光')
        newPlank.matCode = arrs.join('_')
      }
    } else {
      // 拿到材质对应的高光名称
      const highGlossName = matchHighlightBoardName(material || '')
      newPlank.is_high_gloss_plank =
        highGlossCell === translate(getLangeKey('高光板')) ||
        highGlossCell === highGlossName
          ? true
          : false
    }
    switch (String(texDirCell ?? '').trim()) {
      case translate(getLangeKey('竖纹')):
        newPlank.texDir = 'normal'
        newPlank.srcTexDir = 'normal'
        break
      case translate(getLangeKey('横纹')):
        newPlank.texDir = 'reverse'
        newPlank.srcTexDir = 'reverse'
        break
      case translate(getLangeKey('无纹理')):
        newPlank.texDir = 'notcare'
        newPlank.srcTexDir = 'notcare'
        break
      default:
        newPlank.texDir = 'notcare'
        newPlank.srcTexDir = 'notcare'
        break
    }

    newPlank.hsInfo = getPlankCellValue(plank, 'hsInfo', [
      translate(getLangeKey('孔槽数量')),
    ]) as any
    newPlank.texture = getPlankCellValue(plank, 'texture', [
      translate(getLangeKey('颜色')),
    ]) as any
    newPlank.holes = []
    newPlank.slots = []
    newPlank.sholes = []
    newPlank.sslots = []
    newPlank.realCurve = [
      { x: 0, y: 0 },
      { x: newPlank.oRect.width, y: 0 },
      { x: newPlank.oRect.width, y: newPlank.oRect.height },
      { x: 0, y: newPlank.oRect.height },
    ]
    newPlank.name = '导入的板件'
    newPlank.matRotatable = newPlank.texDir == 'notcare' ? true : false
    const { width, height } = newPlank.oRect
    newPlank.finishedArea = toDecimal((width * height) / 1000000, 2)
    plankArr.push(newPlank)
  }
  return { plankList: plankArr }
}

// csv文件数据中可能会包含订单的信息，因此需要先处理订单信息（即是样表信息）
export const dealCsvOrderInfo = (data: any[], from = 'materialList') => {
  let isSpecialHead = data[0] && data[0].split(',').filter((v: any) => v)
  let newData: any[] = data
  let showSample = true
  isSpecialHead.color = '#E7E7E7'
  isSpecialHead = isSpecialHead.length
    ? isSpecialHead.every((v: any) => sampleHead.includes(v))
    : false
  let orderNo = ''
  let orderAddress = ''
  let orderRoomName = ''
  let customer = ''
  let remarks = ''
  const originalData = {
    address: '',
    clientData: '',
    factoryOrderNum: '',
    orderRemark: '',
    roomName: '',
  }
  let sData: any = {}
  if (isSpecialHead) {
    const sample = data[1].split(',').map((it: any) => it.replace(/&/g, ','))
    const template = JSON.parse(JSON.stringify({ ...originalData }))
    Object.keys(template).forEach((key, idx) => {
      template[key] = sample[idx] ?? ''
    })
    sData = template
    newData = data.slice(2)
    orderNo = sample[2]
    orderAddress = sample[0]
    orderRoomName = sample[3]
    customer = sample[1]
    remarks = sample[4]
  } else {
    // 导入表格没有表头两行时，不显示样表头
    showSample = false
  }
  // 筛选分隔之后的空白数据
  const allData = newData
    .filter((v) => v)
    .map((data) => {
      // 去掉数据中的引号和制表符
      return data.replace(/"/g, '').replace(/\t/g, '')
    })
  const arr = []
  // 去除回车符号(直接用console打印单条数据没有这个回车符号, 但是打印整体就有, 没有找到原因, 但是为了保险, 就还是去掉了一下这个回车符号)
  // 获取csv表格的表头
  const keys = allData[0].split(',')
  if (from === 'materialList') {
    // 开料清单的表头处理
    keys.forEach((item: string, index: number) => {
      if (item === '名称') {
        // 自定义表头名后 直接把表头名改了有问题（并且前面读取的时候是有做兼容的 先注释）
        // keys[index] = '板件名称'
      } else if (item === '长度') {
        // keys[index] = '成品长度'
        keys.push('厚度')
      } else if (item === '宽度') {
        // keys[index] = '成品宽度'
      } else if (item === '封边信息') {
        keys.push('前封边')
        keys.push('后封边')
        keys.push('左封边')
        keys.push('右封边')
      }
    })
  }
  // 删除表头
  allData.splice(0, 1)
  // 处理csv表格内容, 将表头和内容一一对应
  for (let i = 0; i < allData.length; i++) {
    const plankInfo = allData[i].split(',')
    // 余料导入的表头和开料清单的表头不一样，余料导入这里不需要这些额外的字段

    const obj: any =
      from === 'surplusStorage'
        ? {}
        : { orderNo, orderAddress, orderRoomName, customer, remarks }
    for (let j = 0; j < plankInfo.length; j++) {
      obj[keys[j]] = plankInfo[j]
    }
    arr.push(obj)
  }
  return {
    data: arr,
    headerItemAddress: sData,
    showSample,
  }
}

// 检测导入的是csv数据的样表头是否完整
export const checkCsvData = (data: any[]) => {
  return new Promise<Record<string, any>[]>((resolve, reject) => {
    const needRows =
      store.state.preferencesSetting?.setting?.needSaveData?.filter(
        (item) =>
          item.prop && indispensableHeaderPropList.includes(item.prop as any)
      ) ?? []

    let keys: string[] = []
    if (typeof data[0] === 'object') {
      keys = Object.keys(data[0])
    }
    const keySet = new Set(keys)

    // 每一列：导入表头中只要出现「自定义表头」或「默认表头」任一即视为不缺失
    const lackDisplayNames: string[] = []
    for (const row of needRows) {
      const customKey = String(translate(row.customLabel as any) ?? '')
      const defaultKey =
        typeof row.label === 'string' ? translate(row.label as any) : ''
      const hasCustom = Boolean(customKey && keySet.has(customKey))
      const hasDefault = Boolean(defaultKey && keySet.has(defaultKey))
      if (hasCustom || hasDefault) {
        continue
      }
      const displayName = (customKey || defaultKey || row.prop) as string
      lackDisplayNames.push(displayName)
    }

    let lackTableString = ''
    let showTableDialog = false
    let isShowLackNeedTableHeader = false
    let isShowLackNeedTableHeaderData = true
    if (lackDisplayNames.length) {
      lackDisplayNames.forEach((item: string, index: number) => {
        if (index !== lackDisplayNames.length - 1) {
          lackTableString += `"${item}、"`
        } else {
          lackTableString += `"${item}"`
        }
      })
      showTableDialog = true
      isShowLackNeedTableHeader = true
      isShowLackNeedTableHeaderData = false
      reject({
        showTableDialog,
        isShowLackNeedTableHeader,
        isShowLackNeedTableHeaderData,
        lackTableString,
      })
    }
    resolve(data)
  })
}

// 检查导入的余料表格文件是否完整
export const checkSurplusCsvData = (data: any[]) => {
  return new Promise<any>((resolve, reject) => {
    let keys: string[] = []
    if (typeof data[0] === 'object') {
      keys = Object.keys(data[0])
    }
    // 旧版余料表不含“修边”列，缺失时按跟随原片标准板材修边值导入
    const requiredHeaders = exportTableHeader.filter(
      (header) => header !== '修边'
    )
    const lackTableHeaderList = getArrMissing(keys, requiredHeaders)
    let lackTableString = ''
    let showTableDialog = false
    // 检查数据的完整性
    // 忽略去检查的数据项
    const ignoreArr = [
      '余料名称',
      '来源订单地址',
      '来源订单号',
      '备注',
      '板件条码',
      '面积',
      // 空修边表示跟随原片标准板材修边值
      '修边',
    ]
    const { emptyFields, invalidFields } = checkFieldValue(data, ignoreArr)
    if (
      lackTableHeaderList.length ||
      emptyFields.length ||
      invalidFields.length
    ) {
      showTableDialog = true
      const errorMessages: string[] = []

      // 收集缺失表头的错误信息
      if (lackTableHeaderList.length) {
        let headerErrorString = ''
        lackTableHeaderList.forEach((item: string, index: number) => {
          if (index !== lackTableHeaderList.length - 1) {
            headerErrorString += `"${item}、"`
          } else {
            headerErrorString += `"${item}"`
          }
        })
        errorMessages.push(headerErrorString)
      }

      // 收集空字段的错误信息
      if (emptyFields.length) {
        let emptyErrorString = ''
        emptyFields.forEach((item: Record<string, any>, index: number) => {
          if (index !== emptyFields.length - 1) {
            emptyErrorString += `第${item.index}行缺失${item.fieldName}、`
          } else {
            emptyErrorString += `第${item.index}行缺失${item.fieldName}`
          }
        })
        errorMessages.push(emptyErrorString)
      }

      // 收集格式不正确的错误信息
      if (invalidFields.length) {
        let invalidErrorString = ''
        invalidFields.forEach((item: Record<string, any>, index: number) => {
          if (index !== invalidFields.length - 1) {
            invalidErrorString += `第${item.index}行${item.fieldName}格式不正确、`
          } else {
            invalidErrorString += `第${item.index}行${item.fieldName}格式不正确`
          }
        })
        errorMessages.push(invalidErrorString)
      }

      // 合并所有错误信息
      lackTableString = errorMessages.join('；')
      reject({
        showTableDialog,
        lackTableString,
      })
    }

    resolve(data)
  })
}

const checkFieldValue = (
  data: Record<string, any>[],
  ignoreOption: string[]
) => {
  // 判断空字段
  const emptyFields: Record<string, any>[] = []

  // 判断内容不对
  const invalidFields: Record<string, any>[] = []

  const checkObject = (obj: Record<string, any>, path = '') => {
    // 先获取板材形状，用于后续判断
    const shape = obj['板材形状']
    const isRectangle = shape === '矩形'

    // 矩形的短边字段允许为空，但不能影响后续 L 形行的必填校验
    const currentIgnoreOption = isRectangle
      ? [...ignoreOption, '短边长度', '短边宽度']
      : ignoreOption

    Object.entries(obj).forEach(([key, value]) => {
      const currentPath = path ? `${path}.${key}` : key
      const isEmpty = value === '' || value === null || value === undefined
      // 先检查空值
      if (isEmpty) {
        if (!currentIgnoreOption.includes(key)) {
          emptyFields.push({ fieldName: key, index: Number(path) + 1 })
        }
        // 如果是空值，跳过后续的格式检查
        if (typeof value === 'object' && !Array.isArray(value)) {
          checkObject(value, currentPath)
        }
        return
      }
      // 字段不为空时，进行格式检查
      // 当形状为矩形时，短边长度和短边宽度不能有值
      if (isRectangle && (key === '短边长度' || key === '短边宽度')) {
        invalidFields.push({
          fieldName: key,
          index: Number(path) + 1,
        })
      } else if (key === '高光板') {
        if (value !== '高光板' && value !== '非高光板') {
          invalidFields.push({ fieldName: key, index: Number(path) + 1 })
        }
      } else if (key === '板材形状') {
        if (value !== '矩形' && value !== 'L形') {
          invalidFields.push({ fieldName: key, index: Number(path) + 1 })
        }
      } else if (
        ['厚度', '短边长度', '短边宽度', '长度', '宽度'].includes(key)
      ) {
        // 如果形状是矩形，短边长度和短边宽度的数字格式检查已在前面处理，这里跳过
        if (isRectangle && (key === '短边长度' || key === '短边宽度')) {
          // 跳过数字格式检查，因为已经在前面检查了是否有值
        } else if (!isNumericPositive(value)) {
          invalidFields.push({ fieldName: key, index: Number(path) + 1 })
        }
      } else if (key === '修边' && !isValidSurplusTrimDisplay(value, shape)) {
        // 修边：/ 或 上0-下5-左0-右5（空在忽略列表中按 / 处理）
        invalidFields.push({ fieldName: key, index: Number(path) + 1 })
      }

      // 处理嵌套对象
      if (typeof value === 'object' && !Array.isArray(value)) {
        checkObject(value, currentPath)
      }
    })
  }

  data.forEach((item, index) => checkObject(item, index.toString()))
  return { emptyFields, invalidFields }
}
const isNumericPositive = (value: any) => {
  const number = Number(value)
  return !isNaN(number) && number > 0
}

/**
 * 获取第一个数组中对于第二个数组中缺少的元素
 * @param arr1 数组1
 * @param arr2 数组2
 * @returns 缺少的元素
 */
const getArrMissing = (arr1: string[], arr2: string[]) => {
  const set1 = new Set(arr1)
  const set2 = new Set(arr2)
  return [...arr2.filter((x) => !set1.has(x))]
}
